import { createContext, useContext, useMemo, ReactNode } from 'react';
import Fuse from 'fuse.js';
import { useAppSelector } from '@/hooks';
import { itemFuseOptions, openItemFuseOptions } from '@/utils/fuzzySearch';
import { Item } from '@/types/models/Item';
import { OpenItem } from '@/types/models/OpenItem';
import { logger } from '@/lib/logger';

// Context type
interface FuseContextType {
  itemFuse: Fuse<Item>;
  openItemFuse: Fuse<OpenItem>;
}

const FuseContext = createContext<FuseContextType | undefined>(undefined);

export const FuseProvider = ({ children }: { children: ReactNode }) => {
  const items = useAppSelector(state => state.items.items);
  const openItems = useAppSelector(state => state.openItems.openItems);

  const itemFuse = useMemo(() => {
    logger.debug('itemFuse instantiated', { data: { count: items.length }, component: 'FuseContext' });
    return new Fuse(items, itemFuseOptions);
  }, [items]);
  const openItemFuse = useMemo(() => {
    logger.debug('openItemFuse instantiated', { data: { count: openItems.length }, component: 'FuseContext' });
    return new Fuse(openItems, openItemFuseOptions);
  }, [openItems]);

  return (
    <FuseContext.Provider value={{ itemFuse, openItemFuse }}>
      {children}
    </FuseContext.Provider>
  );
};

export const useFuse = () => {
  const context = useContext(FuseContext);
  if (!context) throw new Error('useFuse must be used within a FuseProvider');
  return context;
}; 